import { useCallback, useSyncExternalStore, type RefObject } from 'react';

import { useLayoutEffect } from './useLayoutEffect';

interface GridDimensions {
  readonly inlineSize: number;
  readonly blockSize: number;
}

// use unmanaged WeakMaps so we preserve the cache even when
// the component partially unmounts via a suspense boundary
const dimensionsMap = new WeakMap<RefObject<HTMLDivElement>, GridDimensions>();
const targetToRefMap = new WeakMap<HTMLDivElement, RefObject<HTMLDivElement>>();
const subscribers = new Map<RefObject<HTMLDivElement>, () => void>();

// don't break in Node.js (SSR), jsdom, and environments that don't support ResizeObserver
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const resizeObserver = globalThis.ResizeObserver == null ? null : new ResizeObserver(resizeObserverCallback);

function resizeObserverCallback(entries: ResizeObserverEntry[]): void {
  for (const entry of entries) {
    const target = entry.target as HTMLDivElement;
    const ref = targetToRefMap.get(target);

    if (ref !== undefined) {
      const size = entry.contentBoxSize[0];
      updateDimensions(ref, {
        inlineSize: size.inlineSize,
        blockSize: size.blockSize,
      });
    }
  }
}

function updateDimensions(ref: RefObject<HTMLDivElement>, dimensions: GridDimensions): void {
  const prev = dimensionsMap.get(ref);
  if (prev !== undefined && prev.inlineSize === dimensions.inlineSize && prev.blockSize === dimensions.blockSize) {
    return;
  }

  dimensionsMap.set(ref, dimensions);
  subscribers.get(ref)?.();
}

function getServerSnapshot(): undefined {
  return undefined;
}

export function useGridDimensions(gridRef: RefObject<HTMLDivElement>): readonly [number, number, boolean] {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      subscribers.set(gridRef, onStoreChange);

      return () => {
        subscribers.delete(gridRef);
      };
    },
    [gridRef],
  );

  const getSnapshot = useCallback((): GridDimensions | undefined => {
    // gridRef.current is null during the initial render or when suspending;
    // we key the ref object instead to access stable values regardless of rendering state
    return dimensionsMap.get(gridRef);
  }, [gridRef]);

  // We use `useSyncExternalStore` instead of `useState` to avoid tearing,
  // which can lead to flashing scrollbars.
  const dimensions = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useLayoutEffect(() => {
    const target = gridRef.current!;

    targetToRefMap.set(target, gridRef);
    resizeObserver?.observe(target);

    if (!dimensionsMap.has(gridRef)) {
      const { clientWidth, clientHeight } = target;

      updateDimensions(gridRef, {
        inlineSize: clientWidth,
        blockSize: clientHeight,
      });
    }

    return () => {
      resizeObserver?.unobserve(target);
    };
  }, [gridRef]);

  return [dimensions?.inlineSize ?? 1, dimensions?.blockSize ?? 1, dimensions !== undefined] as const;
}
